/**
 * Offline Existing-Asset Sync Worker (v1.1.25)
 * ============================================
 * Background worker that drains the existing-asset offline queue when the
 * device comes online. For each queued op of type
 * `add_images_then_reanalyze`, it sequentially invokes:
 *
 *   1. POST /api/analyses/{analysis_id}/add-images   (existing endpoint)
 *   2. POST /api/analyses/{analysis_id}/reanalyze    (existing endpoint)
 *
 * — the same two endpoints, with the same payloads, that the existing online
 * "Add Photos" + "Re-Analyze" buttons hit when a connected user taps them.
 *
 * DESIGN INVARIANT (per product owner directive 2026-02-06):
 *   This worker MUST NOT modify any existing service. It composes existing
 *   axios calls verbatim. If this file is deleted, the existing offline flow
 *   for NEW assets in services/syncManager.js continues to work unchanged.
 *
 *   The worker is registered via a window 'online' event listener installed
 *   exactly once at app startup. The listener is additive — it does not
 *   replace or interfere with the existing OfflineContext sync listener.
 */
import axios from 'axios';
import { toast } from 'sonner';
import {
  getPendingExistingAssetOps,
  updateOpStatus,
  clearCompletedOps,
} from './offlineExistingAssetQueue';
// v1.3.16 — Manual airplane mode integration. See lib/connectivity.js.
import { isEffectivelyOnline, subscribeConnectivity } from '../lib/connectivity';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

let isRunning = false;
let isInitialized = false;
const listeners = new Set();

/**
 * Pluggable subscriber pattern so the UI can show "syncing N items..." toasts
 * without coupling to this module's internals.
 */
export const subscribeExistingAssetSync = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

const notify = (event) => {
  listeners.forEach(cb => {
    try { cb(event); } catch (e) { /* listener failure must not stop sync */ }
  });
};

/**
 * Drain a single op. Sequentially performs:
 *   add-images  (always)
 *   reanalyze   (skipped when op_type === 'add_photos_only', v1.1.53)
 * with each call wrapped in its own try/catch so a partial success is
 * recorded (e.g. images added but reanalyze fails → retry will skip
 * add-images and only retry reanalyze on the next pass).
 *
 * Returns true on full success, false on any failure.
 */
const drainOne = async (op) => {
  const token = localStorage.getItem('token');
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  try {
    // Stage 1 — add images, unless we already added them on a prior attempt.
    if (!op._images_added) {
      const res = await axios.post(
        `${API}/analyses/${op.analysis_id}/add-photos`,
        { images_base64: op.photos_base64 },
        { headers: authHeader }
      );
      if (res.status !== 200 && res.status !== 201) {
        throw new Error(`add-photos returned ${res.status}`);
      }
      // Mark as added so a partial-failure retry doesn't double-upload.
      op._images_added = true;
    }

    // Stage 2 — reanalyze, unless this op was enqueued as "add photos only"
    // (the v1.1.53 sibling of the original Add+Reanalyze op_type). The
    // simple "Add to Asset" button on the existing-asset card uses the
    // add_photos_only flavour so the on-site team's photos stick offline
    // without triggering AI re-analysis automatically.
    if (op.op_type !== 'add_photos_only') {
      const reanalyzeRes = await axios.post(
        `${API}/analyses/${op.analysis_id}/reanalyze`,
        {},
        { headers: authHeader }
      );
      if (reanalyzeRes.status !== 200 && reanalyzeRes.status !== 201 && reanalyzeRes.status !== 202) {
        throw new Error(`reanalyze returned ${reanalyzeRes.status}`);
      }
    }

    await updateOpStatus(op.id, 'completed');
    return true;
  } catch (err) {
    const msg = err?.response?.data?.detail || err?.message || 'sync failed';
    await updateOpStatus(op.id, 'failed', msg);
    return false;
  }
};

/**
 * Drain the entire queue once. Safe to call concurrently — guarded by
 * isRunning flag. No-op if offline.
 */
export const drainExistingAssetQueue = async () => {
  if (isRunning) return;
  if (!isEffectivelyOnline()) return;
  isRunning = true;

  try {
    const ops = await getPendingExistingAssetOps();
    if (ops.length === 0) {
      isRunning = false;
      return;
    }

    notify({ type: 'started', total: ops.length });

    let succeeded = 0;
    let failed = 0;
    for (const op of ops) {
      // Cap retries — after 5 failures we leave the op in 'failed' state
      // and stop attempting until the user manually retries or the app
      // restarts.
      if ((op.retry_count || 0) >= 5) {
        continue;
      }
      // Mark as syncing for diagnostic visibility, then attempt.
      try { await updateOpStatus(op.id, 'syncing'); } catch (e) { /* non-fatal */ }
      const ok = await drainOne(op);
      if (ok) succeeded++; else failed++;
      notify({ type: 'progress', succeeded, failed, total: ops.length });
    }

    if (succeeded > 0) {
      // Differentiate the toast based on what was actually replayed —
      // add_photos_only ops don't trigger re-analysis, so claiming
      // "photos + re-analysis" would mislead the assessor.
      const photoOnly = ops.filter(o => o.op_type === 'add_photos_only' && o.status === 'completed').length;
      const fullSync = succeeded - photoOnly;
      if (fullSync > 0 && photoOnly > 0) {
        toast.success(
          `Synced ${succeeded} existing asset${succeeded > 1 ? 's' : ''} (${fullSync} with re-analysis, ${photoOnly} photos only)`,
          { duration: 5000 }
        );
      } else if (photoOnly > 0) {
        toast.success(
          `Uploaded ${photoOnly} batch${photoOnly > 1 ? 'es' : ''} of photos to existing assets`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `Synced ${succeeded} existing asset${succeeded > 1 ? 's' : ''} (photos + re-analysis)`,
          { duration: 5000 }
        );
      }
    }
    if (failed > 0) {
      toast.warning(
        `${failed} existing-asset sync${failed > 1 ? 's' : ''} failed — will retry automatically`,
        { duration: 6000 }
      );
    }

    // Housekeeping — remove completed ops so IndexedDB stays small.
    try { await clearCompletedOps(); } catch (e) { /* non-fatal */ }

    notify({ type: 'finished', succeeded, failed });
  } finally {
    isRunning = false;
  }
};

/**
 * One-time global init. Registers an 'online' listener that drains the queue
 * when connectivity returns. Also drains on init if already online (handles
 * the "user opens app while online with queued ops left over from prior
 * session" case).
 *
 * Safe to call multiple times — re-registration is a no-op.
 */
export const initExistingAssetSync = () => {
  if (isInitialized) return;
  isInitialized = true;

  // v1.3.16 — Drain whenever effective-online state flips true.
  // Subsumes both the pre-existing `online` event and the new
  // "user turned off manual airplane mode" trigger via one
  // subscription.
  subscribeConnectivity(({ effectiveOnline }) => {
    if (effectiveOnline) {
      setTimeout(() => { drainExistingAssetQueue(); }, 750);
    }
  });

  // Drain now if we're already online when the app boots.
  if (isEffectivelyOnline()) {
    setTimeout(() => { drainExistingAssetQueue(); }, 2000);
  }
};
